import { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { supabase } from '../services/supabase'
import { useOrganizationId } from '../hooks/useOrganizationId'
import { useOrganization } from '../contexts/OrganizationContext'
import { useSubscriptionStatus } from '../hooks/useSubscriptionStatus'
import { getDemoSessions, isDemoOrganization } from '../services/demoData'
import { hasReachedDemoLimit, saveDemoEntry, initDemoSession, getDemoEntries } from '../services/demoStorage'
import DemoUpgradePrompt from '../components/DemoUpgradePrompt'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Textarea from '../components/Textarea'
import Select from '../components/Select'
import SearchBar from '../components/SearchBar'
import BulkActions from '../components/BulkActions'
import Pagination from '../components/Pagination'
import { Archive, Plus, User, FileText, Clock, X, Trash2, Download, Radio, BarChart3 } from 'lucide-react'
import RichTextEditor from '../components/RichTextEditor'
import SessionAnalytics from '../components/SessionAnalytics'
import { formatDate } from '../utils/helpers'
import { exportSessionsToPDF } from '../utils/pdfExport'
import LiveSessionRecorder from '../components/LiveSessionRecorder'
import { SkeletonContainer, SkeletonListItem } from '../components/SkeletonLoader'
import { useFormPersistence } from '../hooks/useFormPersistence'

const ITEMS_PER_PAGE = 12

const sessionSchema = z.object({
  clientId: z.string().uuid().optional().nullable(),
  carePlanId: z.string().optional(),
  sessionDate: z.string().min(1, 'Session date is required'),
  duration: z.string().optional(),
  focusArea: z.string().optional(),
  outcomeSummary: z.string().min(10, 'Please add an outcome summary'),
})

const Sessions = () => {
  const { t } = useTranslation()
  const [sessions, setSessions] = useState([])
  const [clients, setClients] = useState([])
  const [carePlans, setCarePlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [notesOpen, setNotesOpen] = useState(false)
  const [activeSession, setActiveSession] = useState(null)
  const [progressNotes, setProgressNotes] = useState([])
  const [noteText, setNoteText] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selected, setSelected] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [liveRecorderOpen, setLiveRecorderOpen] = useState(false)
  const [selectedClientForLive, setSelectedClientForLive] = useState(null)
  const [showAnalytics, setShowAnalytics] = useState(false)
  const [analyticsClientId, setAnalyticsClientId] = useState(null)
  const [richTextContent, setRichTextContent] = useState(null)
  const [reachedDemoLimit, setReachedDemoLimit] = useState(false)
  const organizationId = useOrganizationId()
  const { canWrite, organization } = useOrganization()
  const { isDemo } = useSubscriptionStatus()

  // Check demo limit on mount
  useEffect(() => {
    if (isDemo) {
      initDemoSession()
      if (hasReachedDemoLimit('session')) {
        setReachedDemoLimit(true)
      }
    }
  }, [isDemo])

  const formMethods = useForm({
    resolver: zodResolver(sessionSchema),
    defaultValues: {
      clientId: '',
      carePlanId: '',
      sessionDate: new Date().toISOString().slice(0, 16),
      duration: '',
      focusArea: '',
      outcomeSummary: '',
    },
  })

  // Form data persistence
  const { dataRestored, clearSavedData } = useFormPersistence(
    formMethods,
    'sessions_form_data',
    null,
    [] // No fields to exclude
  )

  useEffect(() => {
    if (organizationId) {
      fetchSessions()
      fetchClients()
      fetchCarePlans()
    }
  }, [organizationId])

  const fetchSessions = async () => {
    if (!organizationId) return
    setLoading(true)
    try {
      // For demo organizations, use demo data
      if (isDemo && isDemoOrganization(organization)) {
        const demoSessions = getDemoSessions()
        // Merge with any localStorage entries
        const localEntries = getDemoEntries('session')
        const allSessions = [...demoSessions, ...localEntries]
        setSessions(allSessions)
        setLoading(false)
        return
      }

      const { data, error } = await supabase
        .from('sessions')
        .select('*, clients(first_name, last_name), care_plans(title)')
        .eq('organization_id', organizationId)
        .order('session_date', { ascending: false })

      if (error) throw error
      setSessions(data || [])
    } catch (error) {
      console.error('Error fetching sessions:', error)
      alert(t('sessions.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const fetchClients = async () => {
    if (!organizationId) return
    const { data, error } = await supabase
      .from('clients')
      .select('id, first_name, last_name')
      .eq('organization_id', organizationId)
      .order('first_name')
    if (!error && data) setClients(data)
  }

  const fetchCarePlans = async () => {
    if (!organizationId) return
    const { data, error } = await supabase
      .from('care_plans')
      .select('id, title')
      .eq('organization_id', organizationId)
      .order('title')
    if (!error && data) setCarePlans(data)
  }

  const filteredSessions = useMemo(() => {
    if (!searchTerm) return sessions
    const term = searchTerm.toLowerCase()
    return sessions.filter(
      (session) =>
        `${session.clients?.first_name} ${session.clients?.last_name}`.toLowerCase().includes(term) ||
        session.focus_area?.toLowerCase().includes(term) ||
        session.outcome_summary?.toLowerCase().includes(term) ||
        formatDate(session.session_date).toLowerCase().includes(term)
    )
  }, [sessions, searchTerm])

  const paginatedSessions = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredSessions.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredSessions, currentPage])

  const totalPages = Math.ceil(filteredSessions.length / ITEMS_PER_PAGE)

  const handleDelete = async (ids) => {
    if (!canWrite('sessions')) {
      alert('You do not have permission to delete sessions')
      return
    }
    try {
      const { error } = await supabase
        .from('sessions')
        .delete()
        .in('id', ids)
        .eq('organization_id', organizationId)

      if (error) throw error
      setSelected([])
      fetchSessions()
    } catch (error) {
      console.error('Error deleting sessions:', error)
      alert(t('sessions.deleteError'))
    }
  }

  const handleExport = (ids = null) => {
    exportSessionsToPDF(sessions, ids)
  }

  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]))
  }

  const toggleSelectAll = () => {
    if (selected.length === paginatedSessions.length) {
      setSelected([])
    } else {
      setSelected(paginatedSessions.map((s) => s.id))
    }
  }

  const openForm = () => {
    // Check demo limit before opening form
    if (isDemo && hasReachedDemoLimit('session')) {
      setReachedDemoLimit(true)
      return
    }
    
    formMethods.reset({
      clientId: '',
      carePlanId: '',
      sessionDate: new Date().toISOString().slice(0, 16),
      duration: '',
      focusArea: '',
      outcomeSummary: '',
    })
    setFormOpen(true)
  }

  const openLiveRecorder = () => {
    setLiveRecorderOpen(true)
  }

  const closeLiveRecorder = () => {
    setLiveRecorderOpen(false)
    setSelectedClientForLive(null)
  }

  const handleLiveSessionSave = (session) => {
    fetchSessions()
    closeLiveRecorder()
  }

  const closeForm = () => setFormOpen(false)

  const onSubmit = async (values) => {
    if (!organizationId) return
    try {
      // Check demo limit
      if (isDemo && hasReachedDemoLimit('session')) {
        setReachedDemoLimit(true)
        setFormOpen(false)
        return
      }

      // For demo users, save to localStorage instead of database
      if (isDemo) {
        const demoEntry = {
          client_id: values.clientId,
          care_plan_id: values.carePlanId || null,
          session_date: new Date(values.sessionDate).toISOString(),
          duration_minutes: values.duration ? Number(values.duration) : null,
          focus_area: values.focusArea || null,
          outcome_summary: values.outcomeSummary,
        }
        
        saveDemoEntry('session', demoEntry)
        clearSavedData()
        formMethods.reset()
        closeForm()
        fetchSessions()
        return
      }

      const payload = {
        organization_id: organizationId,
        client_id: values.clientId || null,
        care_plan_id: values.carePlanId || null,
        session_date: new Date(values.sessionDate).toISOString(),
        duration_minutes: values.duration ? Number(values.duration) : null,
        focus_area: values.focusArea || null,
        outcome_summary: values.outcomeSummary,
      }

      const { error } = await supabase.from('sessions').insert(payload)
      if (error) throw error

      // Clear saved form data on success
      clearSavedData()
      formMethods.reset()
      closeForm()
      fetchSessions()
    } catch (error) {
      console.error('Error saving session:', error)
      alert(error.message || t('sessions.saveError'))
    }
  }

  const openNotesPanel = async (session) => {
    setActiveSession(session)
    setNotesOpen(true)
    setNoteText('')
    const { data, error } = await supabase
      .from('progress_notes')
      .select('*')
      .eq('session_id', session.id)
      .order('created_at', { ascending: false })
    if (!error && data) setProgressNotes(data)
  }

  const closeNotesPanel = () => {
    setNotesOpen(false)
    setActiveSession(null)
    setProgressNotes([])
  }

  const addProgressNote = async () => {
    if ((!noteText.trim() && !richTextContent) || !activeSession || !organizationId) return
    try {
      const { data, error } = await supabase
        .from('progress_notes')
        .insert({
          organization_id: organizationId,
          session_id: activeSession.id,
          note: noteText.trim() || richTextContent?.plainText || '',
          rich_text_content: richTextContent?.delta || null,
          plain_text_content: richTextContent?.plainText || noteText.trim() || null,
        })
        .select()
        .single()
      if (error) throw error
      setProgressNotes((prev) => [data, ...prev])
      setNoteText('')
      setRichTextContent(null)
    } catch (error) {
      console.error('Error adding note:', error)
      alert(error.message || t('sessions.addNoteError'))
    }
  }

  const clientOptions = useMemo(
    () =>
      clients.map((client) => ({
        value: client.id,
        label: `${client.first_name} ${client.last_name}`,
      })),
    [clients]
  )

  const carePlanOptions = useMemo(
    () => carePlans.map((plan) => ({ value: plan.id, label: plan.title })),
    [carePlans]
  )

  return (
    <div className="section-container">
      {/* Upgrade Prompt for Demo Users - Show proactively */}
      {isDemo && (
        <DemoUpgradePrompt featureName="sessions" limitReached={reachedDemoLimit} />
      )}
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
              <Archive size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1">{t('sessions.title')}</h1>
              <p className="text-gray-600 dark:text-gray-400">
                {t('sessions.subtitle', { count: sessions.length })}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {sessions.length > 0 && (
              <>
                {/* Analytics button hidden - feature not functional yet */}
                {/* <Button
                  variant="secondary"
                  onClick={() => {
                    setShowAnalytics(true)
                    setAnalyticsClientId(null)
                  }}
                  className="flex items-center gap-2"
                >
                  <BarChart3 size={18} />
                  Analytics
                </Button> */}
                <Button variant="secondary" onClick={() => handleExport()} className="flex items-center gap-2 text-sm sm:text-base px-3 sm:px-6 py-2 sm:py-3">
                  <Download size={16} className="sm:w-[18px] sm:h-[18px]" />
                  <span className="hidden sm:inline">{t('sessions.exportAll')}</span>
                  <span className="sm:hidden">{t('common.buttons.export')}</span>
                </Button>
              </>
            )}
            <Button variant="secondary" onClick={openLiveRecorder} className="flex items-center gap-2 text-gray-900 dark:text-white text-sm sm:text-base px-3 sm:px-6 py-2 sm:py-3">
              <Radio size={16} className="sm:w-[18px] sm:h-[18px]" />
              <span className="hidden sm:inline">{t('sessions.liveRecorder')}</span>
              <span className="sm:hidden">{t('sessions.liveRecorder')}</span>
            </Button>
            <Button onClick={openForm} className="flex items-center gap-2 whitespace-nowrap text-sm sm:text-base px-3 sm:px-6 py-2 sm:py-3">
              <Plus size={16} className="sm:w-[20px] sm:h-[20px]" />
              <span className="hidden sm:inline">{t('sessions.newSession')}</span>
              <span className="sm:hidden">{t('sessions.new')}</span>
            </Button>
          </div>
        </div>
      </motion.div>

      {/* Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('sessions.searchPlaceholder')}
        />
      </motion.div>

      {/* Bulk Actions */}
      <BulkActions
        selected={selected}
        onDelete={handleDelete}
        onExport={handleExport}
        onClear={() => setSelected([])}
        resourceName="sessions"
      />

      {loading ? (
        <SkeletonContainer count={6}>
          <SkeletonListItem />
        </SkeletonContainer>
      ) : filteredSessions.length === 0 ? (
        <Card variant="soft" className="p-10 text-center">
          <Archive size={48} className="mx-auto mb-4 text-purple-400" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {searchTerm ? t('sessions.noMatching') : t('sessions.noSessions')}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {searchTerm
              ? t('sessions.adjustSearch')
              : t('sessions.getStarted')}
          </p>
          {!searchTerm && (
            <Button onClick={openForm} className="flex items-center gap-2 whitespace-nowrap">
              <Plus size={20} />
              {t('sessions.createFirst')}
            </Button>
          )}
        </Card>
      ) : (
        <>
          {paginatedSessions.length > 0 && (
            <div className="mb-4 flex items-center gap-2">
              <input
                type="checkbox"
                checked={selected.length === paginatedSessions.length && paginatedSessions.length > 0}
                onChange={toggleSelectAll}
                className="w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('sessions.selectAllPage')}</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedSessions.map((session, index) => (
              <motion.div
                key={session.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card variant="soft" hover className="p-6 h-full flex flex-col relative">
                  {canWrite('sessions') && (
                    <input
                      type="checkbox"
                      checked={selected.includes(session.id)}
                      onChange={() => toggleSelect(session.id)}
                      className="absolute top-4 right-4 w-5 h-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500 z-10"
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-1">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center text-white text-xs shadow-md">
                        <User size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-semibold ${session.archived_at ? 'text-gray-500 dark:text-gray-400 line-through' : 'text-gray-900 dark:text-white'}`}>
                            {session.clients
                              ? `${session.clients.first_name} ${session.clients.last_name}`
                              : session.archived_at
                              ? t('sessions.clientArchived') || 'Client archived'
                              : t('sessions.generalSession') || 'General Session'}
                          </p>
                          {session.archived_at && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                              {t('sessions.archived') || 'Archived'}
                            </span>
                          )}
                          {!session.client_id && !session.archived_at && (
                            <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">
                              {t('sessions.general') || 'General'}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(session.session_date)}</p>
                      </div>
                    </div>
                    {session.duration_minutes && (
                      <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                        <Clock size={14} />
                        {session.duration_minutes} min
                      </span>
                    )}
                  </div>
                  {session.focus_area && (
                    <p className="text-xs uppercase tracking-wide font-semibold text-purple-600 dark:text-purple-400 mb-2">
                      {session.focus_area}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
                    {session.outcome_summary || 'No summary added yet.'}
                  </p>
                  <div className="mt-auto pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                        <FileText size={14} />
                        <span>Notes</span>
                      </span>
                      <button
                        onClick={() => openNotesPanel(session)}
                        className="text-purple-600 hover:text-purple-700 text-xs font-semibold"
                      >
                        View / Add
                      </button>
                    </div>
                    {canWrite('sessions') && (
                      <button
                        onClick={() => {
                          if (confirm('Delete this session? This will also delete all associated notes.')) {
                            handleDelete([session.id])
                          }
                        }}
                        className="w-full mt-2 px-3 py-2 text-red-600 font-semibold hover:bg-red-50 rounded-lg transition-colors text-sm flex items-center justify-center gap-1"
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    )}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={filteredSessions.length}
          />
        </>
      )}

      {/* Session Form Drawer */}
      <AnimatePresence>
        {formOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeForm}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 bottom-0 w-full max-w-lg z-50 p-6 overflow-y-auto glass-card"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-sm uppercase text-gray-400 dark:text-gray-500 font-semibold tracking-wide">{t('sessions.form.record')}</p>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{t('sessions.form.newSession')}</h3>
                </div>
                <button
                  onClick={closeForm}
                  className="p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X size={20} />
                </button>
              </div>

              <FormProvider {...formMethods}>
                {/* Data Restored Indicator */}
                {dataRestored && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-sm text-green-800 dark:text-green-200"
                  >
                    ✓ {t('sessions.form.dataRestored')}
                  </motion.div>
                )}

                <form className="space-y-4" onSubmit={formMethods.handleSubmit(onSubmit)}>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      {t('sessions.form.client')} <span className="text-gray-500 text-xs">({t('sessions.form.optional') || 'Optional'})</span>
                    </label>
                    <Select
                      options={[
                        { label: t('sessions.form.generalSession') || 'General Session (No Client)', value: '' },
                        ...clientOptions
                      ]}
                      error={formMethods.formState.errors.clientId?.message}
                      {...formMethods.register('clientId', {
                        onChange: (e) => {
                          const value = e.target.value || null
                          formMethods.setValue('clientId', value)
                        },
                      })}
                    />
                    {!formMethods.watch('clientId') && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('sessions.form.generalSessionDescription') || 'This session will be available for all clients'}
                      </p>
                    )}
                  </div>
                  <Select
                    label={t('sessions.form.carePlan')}
                    options={carePlanOptions}
                    placeholder={t('sessions.form.optional')}
                    error={formMethods.formState.errors.carePlanId?.message}
                    {...formMethods.register('carePlanId')}
                  />
                  <Input
                    type="datetime-local"
                    label={t('sessions.form.sessionDate')}
                    required
                    error={formMethods.formState.errors.sessionDate?.message}
                    {...formMethods.register('sessionDate')}
                  />
                  <Input
                    type="number"
                    label={t('sessions.form.duration')}
                    min={0}
                    error={formMethods.formState.errors.duration?.message}
                    {...formMethods.register('duration')}
                  />
                  <Input
                    label={t('sessions.form.focusArea')}
                    placeholder={t('sessions.form.focusAreaPlaceholder')}
                    error={formMethods.formState.errors.focusArea?.message}
                    {...formMethods.register('focusArea')}
                  />
                  <Textarea
                    label={t('sessions.form.outcomeSummary')}
                    required
                    placeholder={t('sessions.form.outcomeSummaryPlaceholder')}
                    error={formMethods.formState.errors.outcomeSummary?.message}
                    {...formMethods.register('outcomeSummary')}
                  />
                  <div className="flex gap-3 pt-4">
                    <Button type="submit" className="flex-1">
                      {t('sessions.form.saveSession')}
                    </Button>
                    <Button type="button" variant="secondary" className="flex-1" onClick={closeForm}>
                      {t('sessions.form.cancel')}
                    </Button>
                  </div>
                </form>
              </FormProvider>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Progress Notes Panel */}
      <AnimatePresence>
        {notesOpen && activeSession && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeNotesPanel}
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 200 }}
              className="fixed bottom-0 left-0 right-0 w-full z-50 p-6 bg-white/90 backdrop-blur-xl shadow-2xl rounded-t-3xl max-h-[80vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs uppercase text-gray-400 font-semibold tracking-wide">
                    Progress Notes
                  </p>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {activeSession.clients
                      ? `${activeSession.clients.first_name} ${activeSession.clients.last_name}`
                      : 'Session'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{formatDate(activeSession.session_date)}</p>
                </div>
                <button
                  onClick={closeNotesPanel}
                  className="p-2 rounded-full bg-gray-100 text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mb-4">
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Add Progress Note (Rich Text)
                  </label>
                  <RichTextEditor
                    value={richTextContent}
                    onChange={setRichTextContent}
                    placeholder="Document progress, observations, or next steps..."
                  />
                </div>
                <div className="mb-4">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Or use plain text:</p>
                  <Textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Plain text note..."
                    rows={3}
                  />
                </div>
                <Button
                  onClick={addProgressNote}
                  className="mt-2 flex items-center gap-2 whitespace-nowrap"
                  disabled={!noteText.trim() && !richTextContent}
                >
                  <Plus size={18} />
                  Add Note
                </Button>
              </div>

              <div className="space-y-3">
                {progressNotes.length === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-8">No notes yet</p>
                ) : (
                  progressNotes.map((note) => (
                    <Card key={note.id} variant="soft" className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(note.created_at)}</p>
                      </div>
                      {note.rich_text_content ? (
                        <div
                          className="text-sm text-gray-700 dark:text-gray-300 prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: note.rich_text_content?.ops
                              ?.map((op) => {
                                if (typeof op.insert === 'string') {
                                  let text = op.insert
                                  if (op.attributes) {
                                    if (op.attributes.bold) text = `<strong>${text}</strong>`
                                    if (op.attributes.italic) text = `<em>${text}</em>`
                                    if (op.attributes.underline) text = `<u>${text}</u>`
                                  }
                                  return text
                                }
                                return ''
                              })
                              .join('') || note.note,
                          }}
                        />
                      ) : (
                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{note.note}</p>
                      )}
                    </Card>
                  ))
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Live Session Recorder */}
      <AnimatePresence>
        {liveRecorderOpen && (
          <LiveSessionRecorder
            clientId={selectedClientForLive}
            onClose={closeLiveRecorder}
            onSave={handleLiveSessionSave}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

export default Sessions
